import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories, categoryArticles } from '@/mocks/categoryData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'live');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (slug && categories.find((c) => c.slug === slug)) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const articles = categoryArticles[activeSlug] || [];

  const handleTabClick = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.scrollTo(0, 0);
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://kaiyun-football.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: currentCategory?.label || '分类',
        item: `https://kaiyun-football.com/category/${activeSlug}`,
      },
    ],
  };

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.label || '分类'} - 开云足球`,
    description: currentCategory?.description,
    url: `https://kaiyun-football.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '开云足球',
      url: 'https://kaiyun-football.com/',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://kaiyun-football.com/article/${article.id}`,
      })),
    },
  };

  return (
    <div className="w-full">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionJsonLd) }} />

      <section className="relative w-full h-[280px] md:h-[380px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20football%20stadium%20wide%20shot%20with%20dramatic%20lighting%2C%20dark%20atmosphere%20with%20green%20pitch%20highlights%20and%20warm%20golden%20accents%2C%20professional%20sports%20photography%20style%20with%20rich%20contrast%2C%20energy%20and%20passion%20of%20football%2C%20editorial%20quality%20composition&width=1800&height=500&seq=kaiyun-category-bg&orientation=landscape"
            alt="分类页面"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">
            内容分类
          </h1>
          <p className="text-background-300 text-sm md:text-base max-w-lg mx-auto">
            探索开云足球丰富的足球内容，从赛事直播到数据分析，从互动玩法到精彩活动
          </p>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-1 bg-background-100 p-1 rounded-full overflow-x-auto mb-8 md:mb-10 scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => handleTabClick(cat.slug)}
                className={`flex-shrink-0 px-4 md:px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                }`}
              >
                {cat.label}
              </Link>
            ))}
          </div>

          {currentCategory && (
            <div className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                {currentCategory.label}
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                {currentCategory.description}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group rounded-2xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300"
              >
                <div className="h-44 md:h-52 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                      {article.category}
                    </span>
                    <span className="text-foreground-400 text-xs">{article.date}</span>
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-foreground-500 text-xs md:text-sm leading-relaxed mb-3">
                    {article.summary}
                  </p>
                  <div className="flex items-center gap-1 text-foreground-400 text-xs">
                    <i className="ri-time-line"></i>
                    <span>阅读约 {article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}